import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  split,
  from,
} from "@apollo/client";
import React from "react";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { WebSocketLink } from "@apollo/client/link/ws";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { RetryLink } from "@apollo/client/link/retry";
import auth from "services/auth";
import config from "common/config";
import CryptoJS from "crypto-js";

const getToken = async () => {
  const user = auth.currentUser();
  if (user) {
    const token = await user.jwt(false).catch(console.error);
    return token;
  }

  return null;
};

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await getToken();
  // return the headers to the context so httpLink can read them
  if (token) {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
        // "x-hasura-admin-secret": "asdasd123",
      },
    };
  }

  return {
    headers: {
      ...headers,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    console.error(graphQLErrors);
  }

  if (networkError && networkError.statusCode === 401) {
    // remove cached token on 401 from the server
    console.error(`[Network error]: ${networkError.message}`);
    auth.logoutAndNotify();
  }
});

const httpLink = createHttpLink({
  uri: config.hasura.http,
  fetch: async (uri, options) => {
    const token = await getToken();
    if (token) {
      options.headers.authorization = `Bearer ${token}`;
    }
    if (options.body && process.env.REACT_APP_TO_ENCODE_BODY === "encrypt") {
      const keyutf = CryptoJS.enc.Utf8.parse(
        process.env.REACT_APP_BODY_ENCODING_SECRET
      );
      const iv = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_BODY_ENCODING_IV);

      // const data = options.body;
      // console.log(options.body)
      // console.log(data)
      const encryptedData = CryptoJS.AES.encrypt(options.body, keyutf, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }).toString();

      options.body = encryptedData;
      // console.log("encrypted body:", options.body)
    }
    return fetch(uri, options);
  },
});

const wsClient = new SubscriptionClient(config.hasura.ws, {
  reconnect: true,
  connectionParams: async () => {
    const token = await getToken();
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    };
  },
});
const wsLink = new WebSocketLink(wsClient);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

const retryLink = new RetryLink({
  attempts: (count, operation, error) => {
    const isMutation =
      operation &&
      operation.query &&
      operation.query.definitions &&
      Array.isArray(operation.query.definitions) &&
      operation.query.definitions.some(
        def =>
          def.kind === "OperationDefinition" &&
          def.operation === "mutation"
      );
    if (isMutation) {
      return !!error && count < 25;
    }
    return !!error && count < 6;
  },
});

export const client = new ApolloClient({
  link: from([errorLink, authLink, retryLink, splitLink]),
  cache: new InMemoryCache({
    possibleTypes: {
      // UpdateReservationCheckResult: [
      //   "InvalidAccount",
      //   "InvalidInterval",
      //   "InvalidReservation",
      //   "InvalidRole",
      //   "InvalidVehicle",
      //   "UpdateReservationCheckSuccess",
      //   "VehicleNotAvailable",
      // ],
      Price: [
        "InvalidVehicle",
        "InvalidAccount",
        "InvalidInterval",
        "GetPriceSuccess",
      ],
    },
  }),
});

const Apollo = ({ children }) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Apollo;
